/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import logo from '../../assets/logo.svg';
import { keyframes } from '@emotion/react';
import { translations } from '../../../utils/translations';

// Define the animation
const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

// Add pulsing animation for the CTA buttons
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

function InstagramAccountSetupComponent() {
  const [searchParams] = useSearchParams();
  const lang = searchParams.get('lang') === 'es' ? 'es' : 'en';
  const t = useMemo(() => translations[lang].instagram, [lang]);
  return (
    <div className="min-h-screen bg-[#7B1C14] py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="text-center mb-8">
          <img 
            src={logo} 
            alt="Spicy Cubes" 
            className="h-16 sm:h-20 md:h-24 mx-auto"
          />
        </div>
        
        <div className="bg-[#6E1912] rounded-lg shadow-lg p-8">
          <div className="relative pb-6 mb-6 border-b border-[#F7E8CC]/10">
            <h1 className="text-3xl font-bold text-[#F7E8CC] text-center">{t.title}</h1>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-48 h-[2px] bg-gradient-to-r from-transparent via-[#F7E8CC]/10 to-transparent" />
          </div>

          {/* Introduction */}
          <div className="mt-6 bg-[#5E150F]/30 p-4 rounded-md border border-[#FFD700]/30">
            <p className="text-[#F7E8CC] font-medium">
              {t.intro}
            </p>
          </div>

          {/* Step 1 */}
          <div className="mt-6 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                1
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">{t.step1Title}</h3>
            </div>
            <div className="ml-11">
              <p className="text-[#F7E8CC]/90" dangerouslySetInnerHTML={{ __html: t.step1Text.replace('BRAND NEW', '<span class="font-bold text-[#FFD700]">BRAND NEW</span>').replace('COMPLETAMENTE NUEVA', '<span class="font-bold text-[#FFD700]">COMPLETAMENTE NUEVA</span>') }} />
            </div>
          </div>

          {/* Step 2 */}
          <div className="mt-4 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                2
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">{t.step2Title}</h3>
            </div>
            <div className="ml-11">
              <p className="text-[#F7E8CC]/90 mb-2">
                {t.step2Text}
              </p>
              <ul className="list-disc pl-5 space-y-1 text-[#F7E8CC]/90">
                <li>tryspicycubes</li>
                <li>needspicycubes</li>
                <li>takespicycubes</li>
              </ul>
              <p className="mt-2 text-[#F7E8CC]/90">
                {t.step2Help}
              </p>
              <div className="mt-3 p-2 bg-[#5E150F]/50 rounded-md border border-[#FFD700]/30">
                <p className="text-[#FFD700] font-medium">{t.step2Tip}</p>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="mt-4 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                3
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">{t.step3Title}</h3>
            </div>
            <div className="ml-11">
              <p className="text-[#F7E8CC]/90">
                {t.step3Text}
              </p>
            </div>
          </div>

          {/* Step 4 */}
          <div className="mt-4 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                4
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">{t.step4Title}</h3>
            </div>
            <div className="ml-11">
              <ol className="list-decimal pl-5 space-y-1 text-[#F7E8CC]/90">
                {t.step4Steps.map((step, index) => (
                  <li key={index} dangerouslySetInnerHTML={{ 
                    __html: step
                      .replace('Edit profile', '<span class="text-[#FFD700] font-medium">Edit profile</span>')
                      .replace('Editar perfil', '<span class="text-[#FFD700] font-medium">Editar perfil</span>')
                      .replace('Switch to a professional account', '<span class="text-[#FFD700] font-medium">Switch to a professional account</span>')
                      .replace('Cambiar a cuenta profesional', '<span class="text-[#FFD700] font-medium">Cambiar a cuenta profesional</span>')
                      .replace('digital creator', '<span class="text-[#FFD700] font-medium">digital creator</span>')
                      .replace('creador digital', '<span class="text-[#FFD700] font-medium">creador digital</span>')
                  }} />
                ))}
              </ol>
            </div>
          </div>

          {/* Step 5 */}
          <div className="mt-4 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                5
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">{t.step5Title}</h3>
            </div>
            <div className="ml-11">
              <p className="text-[#F7E8CC]/90 mb-3">
                {t.step5Text}
              </p>
              <div className="p-3 bg-[#5E150F]/50 rounded-md border border-[#FFD700]/30 font-medium text-[#F7E8CC]">
                {t.step5Bio.split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line.includes('insert your discount code') || line.includes('inserta tu código de descuento') ? (
                      <><span className="italic">{line}</span><br/></>
                    ) : (
                      <>{line}<br/></>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <p className="mt-3 text-[#F7E8CC]/90">
                <span className="font-bold">{t.step5Link}</span>
              </p>
            </div>
          </div>

          {/* Step 6 - Engagement */}
          <div className="mt-4 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                6
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">{t.step6Title}</h3>
            </div>
            <div className="ml-11">
              <p className="text-[#F7E8CC]/90 mb-2">
                {t.step6Text}
              </p>
              <ul className="list-disc pl-5 space-y-2 text-[#F7E8CC]/90">
                {t.step6Steps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Comment Filtering */}
          <div className="mt-6 bg-[#5E150F]/70 p-5 rounded-md">
            <h3 className="font-bold text-lg mb-3 text-[#FFD700]">{t.filterTitle}</h3>
            <p className="text-[#F7E8CC]/90 mb-3">
              {t.filterText.split('→').map((part, index, arr) => (
                index === arr.length - 1 ? 
                  <span key={index} className="font-medium">{part}</span> : 
                  <React.Fragment key={index}>{index === 0 ? part : <span className="font-medium">{part}</span>} → </React.Fragment>
              ))}
            </p>
            
            <p className="text-[#F7E8CC]/90 mb-2">{t.filterWords}</p>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">satire</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">infomercial</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">commercial</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">branded</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">scams</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">scam</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">anything</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">doesn't</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">doesnt</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">pitch</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">sale</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">sales</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">sell</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">came</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">never</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">paid</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">advertising</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">advertised</div>
            </div>
          </div>

          {/* Help Section */}
          <div className="mt-6 bg-[#5E150F]/30 p-4 rounded-md border border-[#FFD700]/30">
            <p className="text-center text-[#F7E8CC]/90">
              {t.helpText}
            </p>
          </div>
          
          <div className="mt-8 text-center">
            <a 
              href="/content-brief" 
              css={{
                animation: `${pulse} 2s ease-in-out infinite`,
              }}
              className="inline-flex justify-center rounded-md bg-[#FFB700] py-3 px-6 text-lg font-bold text-[#5E150F] shadow-sm hover:bg-[#FFA200] focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:ring-offset-2 focus:ring-offset-[#7B1C14] transition-colors duration-200"
            >
              {t.backButton}
            </a>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-[#F7E8CC]/80 text-center">
            {t.contactText}{' '}
            <a 
              href="mailto:ashli@spicycubes.co" 
              className="font-bold text-[#F7E8CC] hover:text-[#F7E8CC] whitespace-nowrap underline decoration-[2px] hover:decoration-[2.5px] transition-all duration-200 [text-decoration-skip-ink:none]"
            >
              ashli@spicycubes.co
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export const InstagramAccountSetup = InstagramAccountSetupComponent; 